import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Player } from '@/types';
import { Users, Edit2, ClipboardList, Eye, Check, X, Trash2, RotateCcw } from 'lucide-react';

interface RosterTableProps {
  players: Player[];
  onEdit: (player: Player) => void;
  onToggleActive: (playerId: string, currentStatus: boolean) => void;
  onToggleFlag: (player: Player, field: 'published' | 'consentOnFile') => void;
  onRemove: (player: Player) => void;
  onRestore: (player: Player) => void;
  onHardDelete: (player: Player) => void;
}

export const RosterTable: React.FC<RosterTableProps> = ({
  players,
  onEdit,
  onToggleActive,
  onToggleFlag,
  onRemove,
  onRestore,
  onHardDelete,
}) => {
  // Default to the actual Willard roster; the other tracked bowlers are
  // opt-in noise (the Stats program follows kids from other schools too).
  // Removed kids only appear under "show all", with a restore action.
  const [showAll, setShowAll] = useState(false);

  // Willard team kids first, then alphabetical
  const sortedPlayers = [...players]
    .filter((p) => (showAll ? true : p.willardTeam && p.removed !== true))
    .sort((a, b) => {
      const aTeam = a.willardTeam ? 0 : 1;
      const bTeam = b.willardTeam ? 0 : 1;
      if (aTeam !== bTeam) return aTeam - bTeam;
      return a.name.localeCompare(b.name);
    });

  const displayAverage = (player: Player): string => {
    const avg = player.statsSnapshot?.average ?? player.averageScore;
    return avg ? Math.round(avg).toString() : '--';
  };

  return (
    <div className="bg-white rounded-2xl shadow-tiger-lg overflow-hidden">
      <div className="p-6 border-b border-tiger-neutral-200 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-black text-tiger-primary-black">
            Team Roster
          </h2>
          <p className="text-sm text-tiger-neutral-500 mt-1">
            {players.filter(p => p.willardTeam).length} Willard team · {players.length} tracked total
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold text-tiger-neutral-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="w-4 h-4 rounded accent-black"
          />
          Show all tracked bowlers
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-tiger-neutral-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Player
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Team
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Grade
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Average
              </th>
              <th className="px-6 py-3 text-center text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Published
              </th>
              <th className="px-6 py-3 text-center text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Consent
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-bold text-tiger-neutral-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-tiger-neutral-200">
            {sortedPlayers.map(player => {
              const publishBlocked = !player.published && !player.consentOnFile;
              return (
                <tr key={player.id} className="hover:bg-tiger-neutral-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-tiger-primary-black to-tiger-neutral-700 flex items-center justify-center overflow-hidden">
                        {player.photoURL ? (
                          <img
                            src={player.photoURL}
                            alt={player.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-white font-bold text-sm">
                            {player.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="font-semibold text-tiger-primary-black">
                          {player.name}
                        </div>
                        <div className="text-sm text-tiger-neutral-500">
                          {player.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {player.removed ? (
                      <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                        removed
                      </span>
                    ) : player.willardTeam ? (
                      <span className="px-2.5 py-1 bg-willard-taupe text-tiger-primary-black rounded-full text-xs font-bold">
                        Willard
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 bg-tiger-neutral-200 text-tiger-neutral-600 rounded-full text-xs font-bold">
                        tracked
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 bg-tiger-neutral-100 text-tiger-neutral-700 rounded text-sm font-semibold">
                      {player.grade || '--'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-tiger-primary-black font-semibold">
                    {displayAverage(player)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => onToggleFlag(player, 'published')}
                      title={
                        publishBlocked
                          ? 'consent form required first'
                          : player.published
                          ? 'Unpublish recruiting profile'
                          : 'Publish recruiting profile'
                      }
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                        player.published
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : publishBlocked
                          ? 'bg-tiger-neutral-100 text-tiger-neutral-300 cursor-not-allowed'
                          : 'bg-tiger-neutral-100 text-tiger-neutral-500 hover:bg-tiger-neutral-200'
                      }`}
                    >
                      {player.published ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => onToggleFlag(player, 'consentOnFile')}
                      title={
                        player.consentOnFile
                          ? 'Consent form on file'
                          : 'Mark consent form as on file'
                      }
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                        player.consentOnFile
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-tiger-neutral-100 text-tiger-neutral-500 hover:bg-tiger-neutral-200'
                      }`}
                    >
                      {player.consentOnFile ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => onToggleActive(player.id, player.isActive)}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                        player.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {player.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onEdit(player)}
                        className="p-2 text-tiger-neutral-600 hover:text-tiger-primary-black hover:bg-tiger-neutral-100 rounded transition-colors"
                        title="Edit player"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <Link
                        to={`/coach/player/${player.id}/recruiting`}
                        className="p-2 text-tiger-neutral-600 hover:text-tiger-primary-black hover:bg-tiger-neutral-100 rounded transition-colors"
                        title="Recruiting profile editor"
                      >
                        <ClipboardList className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/player/${player.id}`}
                        className="p-2 text-tiger-neutral-600 hover:text-tiger-primary-black hover:bg-tiger-neutral-100 rounded transition-colors"
                        title="View public profile"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      {player.removed ? (
                        <>
                          <button
                            onClick={() => onRestore(player)}
                            className="p-2 text-tiger-neutral-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                            title="Restore player"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onHardDelete(player)}
                            className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded transition-colors"
                            title="Delete permanently (graduated / left the program)"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => onRemove(player)}
                          className="p-2 text-tiger-neutral-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove from program"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {players.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-tiger-neutral-300 mx-auto mb-4" />
          <p className="text-tiger-neutral-600">No players in roster yet</p>
        </div>
      )}
    </div>
  );
};
